import { lazy, Suspense, useMemo, useState } from "react";
import { Calendar } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { useLeaveApplicationsListPage } from "@/features/hr/leave-applications";
import { useGetTeamLeaveScheduleQuery } from "@/shared/api/teamLeaveSchedule";
import { EmptyState, Pagination } from "@/shared/components";
import LeaveScheduleModal from "@/shared/components/LeaveScheduleModal";
import { Button } from "@/shared/ui";
import { getLeaveScheduleWindow } from "@/shared/utils/leaveSchedule";
import {
  LeaveApplicationsHeader,
  LeaveApplicationsFilter,
  LeaveApplicationsCardsSkeleton,
  LeaveApplicationsPageSkeleton,
} from "./components";

const LeaveApplicationsTableView = lazy(() => import("./components/LeaveApplicationsTableView").then((m) => ({ default: m.default })));
const LeaveApplicationsCardsView = lazy(() => import("./components/LeaveApplicationsCardsView").then((m) => ({ default: m.default })));

export default function LeaveApplicationsPage() {
  const { t } = useTranslation("LeaveApplicationsPage");

  const pageData = useLeaveApplicationsListPage();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const scheduleRange = useMemo(() => getLeaveScheduleWindow(), []);
  const {
    data: leaveSchedule = [],
    isLoading: isScheduleLoading,
    isError: isScheduleError,
  } = useGetTeamLeaveScheduleQuery(scheduleRange);

  const scheduleAction = (
    <Button
      type="button"
      variant="secondary"
      size="huge"
      disabled={isScheduleLoading || isScheduleError}
      onClick={() => setShowScheduleModal(true)}>
      <Calendar size={18} color="currentColor" />
      {t("header.leaveScheduleButton")}
    </Button>
  );

  if (pageData.isLoading && pageData.allLeaves.length === 0) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <LeaveApplicationsHeader>{scheduleAction}</LeaveApplicationsHeader>
          <LeaveApplicationsPageSkeleton />
        </section>
      </>
    );
  }

  if (pageData.isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <LeaveApplicationsHeader>{scheduleAction}</LeaveApplicationsHeader>
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>

      <section className="flex-1 min-w-0">
        <LeaveApplicationsHeader>{scheduleAction}</LeaveApplicationsHeader>

        <div className="radius-3xl surface-base-fill p-7">
          <LeaveApplicationsFilter
            search={pageData.search}
            onSearchChange={pageData.setSearch}
            dateRange={pageData.dateRange}
            onDateRangeChange={pageData.setDateRange}
            statusFilter={pageData.statusFilter}
            onStatusChange={pageData.setStatusFilter}
            statusOptions={pageData.statusOptions}
            hasActiveFilters={pageData.activeFilters}
            onReset={pageData.handleResetFilters}
            locale={pageData.locale}
            view={pageData.view}
            onViewChange={pageData.setView}
          />

          {pageData.filteredLeaves.length === 0 ? (
            <EmptyState
              hasActiveFilters={pageData.activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              {pageData.view === "table" ? (
                <Suspense fallback={<LeaveApplicationsPageSkeleton variant="tableOnly" />}>
                  <LeaveApplicationsTableView
                    leaves={pageData.pageLeaves}
                    sortConfig={pageData.sortConfig}
                    onSort={pageData.handleSort}
                    locale={pageData.locale}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<LeaveApplicationsCardsSkeleton />}>
                  <LeaveApplicationsCardsView leaves={pageData.pageLeaves} locale={pageData.locale} />
                </Suspense>
              )}

              <Pagination
                currentPage={pageData.pagination.page}
                totalPages={pageData.pagination.totalPages}
                onPageChange={pageData.pagination.setPage}
                fromItem={pageData.pagination.fromItem}
                toItem={pageData.pagination.toItem}
                total={pageData.filteredLeaves.length}
              />
            </>
          )}
        </div>
      </section>

      {showScheduleModal && (
        <LeaveScheduleModal
          locale={pageData.locale}
          items={leaveSchedule}
          title={t("schedule.title")}
          getLeaveTypeLabel={(leaveType) => t(`schedule.leaveType.${leaveType}`)}
          formatMoreLeaves={(count) => t("schedule.moreLeaves", { count })}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </>
  );
}
